/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, Heading, IComponentNodeProps, IModuleProps, INodeProps,
    ISingleSlideCarouselProps, ITelemetryContent,
    NodeTag, SingleSlideCarousel } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IInteractiveFeatureCollectionProps, ITitleData } from './interactive-feature-collection.props.autogenerated';

/**
 * Interactive Feature Collection view props.
 */
export interface IInteractiveFeatureCollectionViewProps extends IInteractiveFeatureCollectionProps<{}> {
    title: React.ReactNode;
    interactiveFeatureCollectionHeading: INodeProps;
    interactiveFeatureCollectionContainer: IModuleProps;
    interactiveFeatureCollectionDesktopContainer: INodeProps;
    interactiveFeatureCollectionDesktopItems: React.ReactNode[];
    interactiveFeatureCollectionMobileContainer: INodeProps;
    interactiveFeatureCollectionMobileItems: React.ReactNode[];
    singleSlideCarouselComponentProps: INodeProps;
    singleSlideCarouselItemContainer: INodeProps;
}

/**
 *
 * InteractiveFeatureCollection component.
 * @extends {React.PureComponent<IInteractiveFeatureCollectionProps<{}>>}
 */
export class InteractiveFeatureCollection extends React.PureComponent<IInteractiveFeatureCollectionProps<{}>> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry);

    public constructor(props: IInteractiveFeatureCollectionProps<IInteractiveFeatureCollectionViewProps>) {
        super(props);
    }

    private static _createHeading(heading?: ITitleData): React.ReactNode | null {
        if (!heading || !heading.text || StringExtensions.isNullOrWhitespace(heading.text)) {
            return null;
        }
        return (
            <Heading
                headingTag={heading.tag}
                text={heading.text}
                className='ms-interactive-feature__title'
            />
        );
    }

    private static _getFeatureItem(item: React.ReactNode, index: number, itemsCounter: number): React.ReactNode {
        const keyIndex = `interactive-feature-mosaic-item__${index}`;
        return (
            <React.Fragment key={keyIndex}>
                {React.cloneElement(item as React.ReactElement, { indexItem: index, itemCounter: itemsCounter, isMosaic: true })}
            </React.Fragment>
        );
    }

    public render(): JSX.Element | null {
        const { resources } = this.props;
        const { title, className } = this.props.config;
        const interactiveItems = this.props.slots.interactiveFeatureItems;
        const itemsFeatureCounter = interactiveItems.length;
        const singleSlideCarouselComponentProps: IComponentNodeProps<ISingleSlideCarouselProps> = {
            tag: SingleSlideCarousel,
            className: this.props.config.className ?? '',
            flipperPrevLabel: resources.flipperPrevious,
            flipperNextLabel: resources.flipperNext,
            parentId: this.props.id,
            telemetryContent: this._telemetryContent,
            vertical: false
        };
        const initItemsFeatureCounter = 0;
        const viewProps = {
            ...this.props,
            title: InteractiveFeatureCollection._createHeading(title),
            interactiveFeatureCollectionHeading: {
                className: 'ms-interactive-feature-collection__heading'
            },
            interactiveFeatureCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-interactive-feature-collection', className)
            },
            interactiveFeatureCollectionDesktopContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-desktop'
            },
            interactiveFeatureCollectionMobileContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-mobile'
            },
            singleSlideCarouselComponentProps,
            singleSlideCarouselItemContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-interactive-feature-collection-carousel',
                role: 'listitem'
            },
            interactiveFeatureCollectionMobileItems: this.props.slots.interactiveFeatureItems,
            interactiveFeatureCollectionDesktopItems: itemsFeatureCounter > initItemsFeatureCounter &&
            interactiveItems.map((item: React.ReactNode, index: number) => InteractiveFeatureCollection._getFeatureItem(item, index, itemsFeatureCounter))
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

}

export default InteractiveFeatureCollection;
